import { Box, Typography } from '@mui/material';
import DatePicker from 'components/date-picker/date-picker';
import NumberField from 'components/number-field';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC, useRef } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HUD_1 } from 'utils/constants';
import { LoanFormType } from 'views/orders/loans/types';
import { getLoanPurpose, getLoanTypes } from 'views/orders/loans/utils/constants';
import {
  estimateFirstPaymentDate,
  estimateLastPaymentDate,
  estimateLoanTerm,
  hasValidLoanDate,
} from 'views/orders/loans/utils/term-dates';

import { useStyles } from './styles';
import debounce from 'debounce';

type Props = {
  loanForm: UseFormReturn<LoanFormType, object>;
  isDataLoading: boolean;
  orderType: string;
  onSave: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
};

export const TermsAndDates: FC<Props> = ({ loanForm, isDataLoading, orderType, onSave }) => {
  const { control, setValue } = loanForm;
  const {
    loan_number,
    payment_first_date,
    payment_last_date,
    term_years = 0,
    term_months = 0,
  } = useWatch({ control });
  const { t } = useTranslation();
  const classes = useStyles();
  const durationDateModeRef = useRef<'first' | 'last' | null>(null);
  const debouncedSave = debounce(() => onSave(), 1000);

  const hasFirstPaymentDate = hasValidLoanDate(payment_first_date);
  const hasLastPaymentDate = hasValidLoanDate(payment_last_date);
  const hasPaymentDate = hasFirstPaymentDate || hasLastPaymentDate;

  const getDurationDateMode = () => {
    if (durationDateModeRef.current) return durationDateModeRef.current;

    if (hasFirstPaymentDate) {
      durationDateModeRef.current = 'first';
    } else if (hasLastPaymentDate) {
      durationDateModeRef.current = 'last';
    }

    return durationDateModeRef.current;
  };

  const updateEstimatedPaymentDate = (
    years: number | null | undefined,
    months: number | null | undefined,
  ) => {
    const dateMode = getDurationDateMode();
    if (dateMode === 'first') {
      const estimatedDate = estimateLastPaymentDate(payment_first_date, years, months);
      if (estimatedDate) setValue('payment_last_date', estimatedDate, { shouldDirty: true });
      return;
    }

    if (dateMode === 'last') {
      const estimatedDate = estimateFirstPaymentDate(payment_last_date, years, months);
      if (estimatedDate) setValue('payment_first_date', estimatedDate, { shouldDirty: true });
    }
  };

  const handleDurationBlur = (onBlur: () => void) => {
    onBlur();
    onSave().finally(() => {
      durationDateModeRef.current = null;
    });
  };

  const updateEstimatedLastDate = (
    firstDate: string,
    years: number | null | undefined,
    months: number | null | undefined,
  ) => {
    const estimatedDate = estimateLastPaymentDate(firstDate, years, months);
    if (estimatedDate) {
      setValue('payment_last_date', estimatedDate, { shouldDirty: true });
    }
  };

  const updateEstimatedTerm = (firstDate: string, lastDate: string) => {
    const term = estimateLoanTerm(firstDate, lastDate);
    if (term) {
      setValue('term_years', term.years, { shouldDirty: true });
      setValue('term_months', term.months, { shouldDirty: true });
    }
  };
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box className={classes.fieldRow}>
        <Controller
          control={control}
          name="loan_number"
          render={({ field }) => (
            <TextField
              color="secondary"
              label={<Typography variant="body2">{t('orders:loan-number')}</Typography>}
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="insurance_case"
          key={`insurance_case_${loan_number}`}
          render={({ field }) => (
            <TextField
              color="secondary"
              label={<Typography variant="body2">{t('loan:case-inst-mortgage')}</Typography>}
              name="mortgage_ins_case"
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              value={field.value}
              onChange={field.onChange}
              disabled={isDataLoading}
            />
          )}
        />
        <Controller
          control={control}
          name="buyer_issue_date"
          render={({ field }) => (
            <DatePicker
              label={t('loan:buyer-issue-date')}
              disableToolbar={false}
              name="buyer-cd-issue-date"
              error={false}
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                debouncedSave();
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="seller_issue_date"
          render={({ field }) => (
            <DatePicker
              label={t('loan:seller-issue-date')}
              disableToolbar={false}
              name="seller-cd-issue-date"
              error={false}
              handleBlur={() => {
                field.onBlur();
              }}
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                debouncedSave();
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="commitment_date"
          render={({ field }) => (
            <DatePicker
              label={t('loan:mortgage-commitment-date')}
              disableToolbar={false}
              name="mortgage-commitment-date"
              error={false}
              handleBlur={() => {
                field.onBlur();
              }}
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                debouncedSave();
              }}
            />
          )}
        />

      </Box>

      <Box className={classes.fieldRow}>
        <Controller
          control={control}
          name="terms"
          render={({ field }) => (
            <SelectField
              label={t('loan:loan_type')}
              value={field.value || ''}
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              handleChange={(e) => field.onChange(e.target.value)}
              data={getLoanTypes(t)}
              dataKey=""
              dataValue=""
              dataText=""
              disabled={isDataLoading}
            />
          )}
        />

        {orderType !== HUD_1 && (
          <Controller
            control={control}
            name="purpose"
            render={({ field }) => (
              <SelectField
                label={t('loan:purpose')}
                value={field.value || ''}
                handleBlur={() => {
                  field.onBlur();
                  onSave();
                }}
                handleChange={(e) => field.onChange(e.target.value)}
                data={getLoanPurpose(t)}
                dataKey=""
                dataValue=""
                dataText=""
                disabled={isDataLoading}
                inputLabelProps={{ shrink: !!field.value }}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="payment_first_date"
          render={({ field }) => (
            <DatePicker
              label={t('loan:first-payment-date')}
              disableToolbar={false}
              name="first-payment"
              error={false}
              handleBlur={() => {
                field.onBlur();
              }}
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                if (date && hasValidLoanDate(date)) {
                  durationDateModeRef.current = 'first';
                  updateEstimatedLastDate(date, term_years, term_months);
                }
                debouncedSave();
              }}
              inputRef={field.ref}
            />
          )}
        />
        <Controller
          control={control}
          name="payment_last_date"
          render={({ field }) => (
            <DatePicker
              label={t('loan:last-payment-date')}
              disableToolbar={false}
              name="last-payment"
              error={false}
              minDate={hasFirstPaymentDate ? payment_first_date : undefined}
              handleBlur={() => {
                field.onBlur();
              }}
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                if (date && hasFirstPaymentDate) {
                  durationDateModeRef.current = 'first';
                  updateEstimatedTerm(payment_first_date || '', date);
                } else if (date) {
                  durationDateModeRef.current = 'last';
                  const estimatedDate = estimateFirstPaymentDate(date, term_years, term_months);
                  if (estimatedDate) {
                    setValue('payment_first_date', estimatedDate, { shouldDirty: true });
                  }
                }
                debouncedSave();
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="grace_days"
          render={({ field }) => (
            <NumberField
              variant="filled"
              color="secondary"
              name="grace-period-days"
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              value={field.value}
              label={t('orders:gracePeriodDaysLabel')}
              onChange={(e) => field.onChange(Number(e.target.value))}
            />
          )}
        />
      </Box>

      <Box className={classes.fieldRow}>
        {orderType !== HUD_1 && (
          <>
            <Controller
              control={control}
              name="product"
              key={`product_${loan_number}`}
              render={({ field }) => (
                <TextField
                  color="secondary"
                  label={<Typography variant="body2">{t('loan:product')}</Typography>}
                  name="product"
                  handleBlur={() => {
                    field.onBlur();
                    onSave();
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isDataLoading}
                />
              )}
            />

          </>
        )}
        <Controller
          control={control}
          name="term_years"
          render={({ field }) => (
            <NumberField
              variant="filled"
              color="secondary"
              name="loan_term_years"
              handleBlur={() => {
                handleDurationBlur(field.onBlur);
              }}
              disabled={!hasPaymentDate || isDataLoading}
              value={field.value}
              decimalScale={0}
              allowNegative={false}
              onValueChange={({ floatValue }) => {
                const years = floatValue ?? 0;
                field.onChange(years);
                if (floatValue !== undefined) {
                  updateEstimatedPaymentDate(years, term_months);
                }
              }}
              InputProps={{
                className: classes.moyearAdornment,
                endAdornment: (
                  <Box className={classes.endAdornmentStyle}>
                    <Typography variant="body2">{t('loan:years')}</Typography>
                  </Box>
                ),
                classes: { input: classes.stretchInput },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="term_months"
          render={({ field }) => (
            <NumberField
              variant="filled"
              color="secondary"
              name="loan_term_months"
              handleBlur={() => {
                handleDurationBlur(field.onBlur);
              }}
              disabled={!hasPaymentDate || isDataLoading}
              value={field.value}
              decimalScale={0}
              allowNegative={false}
              onValueChange={({ floatValue }) => {
                const months = floatValue ?? 0;
                field.onChange(months);
                if (floatValue !== undefined) {
                  updateEstimatedPaymentDate(term_years, months);
                }
              }}
              InputProps={{
                className: classes.moyearAdornment,
                endAdornment: (
                  <Box className={classes.endAdornmentStyle}>
                    <Typography variant="body2">{t('loan:Mo')}</Typography>
                  </Box>
                ),
                classes: { input: classes.stretchInput },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="late_payment_days"
          render={({ field }) => (
            <NumberField
              variant="filled"
              color="secondary"
              name="late-payment-days"
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              label={t('loan:days-passed')}
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
            />
          )}
        />
      </Box>

      <Box className={classes.halfWidthFieldRow}>
        <Controller
          control={control}
          name="days_per_year"
          render={({ field }) => (
            <SelectField
              label={t('loan:days_per_year')}
              value={field.value != null ? String(field.value) : ''}
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              handleChange={(e) => {
                const val = e.target.value === '' ? null : Number(e.target.value);
                field.onChange(val);
              }}
              data={[
                { value: '', label: '' },
                { value: 365, label: '365' },
                { value: 360, label: '360' },
                { value: 366, label: '366' },
              ]}
              dataKey="value"
              dataValue="value"
              dataText="label"
              disabled={isDataLoading}
            />
          )}
        />
        <Controller
          control={control}
          name="penalty_as_percent"
          render={({ field }) => (
            <SelectField
              label={t('loan:late-penalty-as')}
              value={field.value != null ? String(field.value) : ''}
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              handleChange={(e) => {
                field.onChange(e.target.value);
              }}
              data={[
                { value: true, label: t('common:percent') },
                { value: false, label: t('common:amount') },
              ]}
              dataKey="value"
              dataValue="value"
              dataText="label"
              disabled={isDataLoading}
            />
          )}
        />
        <Controller
          control={control}
          name="decimals"
          render={({ field }) => (
            <NumberField
              variant="filled"
              color="secondary"
              name="late-payment-days"
              handleBlur={() => {
                field.onBlur();
                onSave();
              }}
              label={t('orders:decimalsLabel')}
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
            />
          )}
        />
      </Box>
    </Box>
  );
};
